import {
    LoopscalePointsAction,
    StrategyExpanded,
    usePortfolioRewardsUtils,
    useLoanMultipliersUtil,
    useRewardsPointsPerSecond,
    useLatestCollateralYieldHistoryByMint,
    BsMetaUtil
} from "@bridgesplit/abf-react";
import {
    Column,
    RewardIcon,
    Row,
    StatColumn,
    StatProps,
    StatWithIcon,
    Text,
    TextColor,
    Tooltip,
    useAppPalette
} from "@bridgesplit/ui";
import { formatMultiplier, formatNum, formatPercent, Hidable, roundAutoToDecimals } from "@bridgesplit/utils";
import { Divider } from "@mui/material";
import { ExternalYieldSource, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { calculateStrategyUtilization } from "app/components/strategy";
import {
    calculateActiveStrategyDailyPoints,
    getStrategyIdleCapMultiplier,
    getStrategyLendMultiplier,
    getStrategyLoopscaleActions
} from "app/components/portfolio/stats";
import { COPY } from "app/constants";

import { ExternalRewardsDisplay, TooltipContent } from "./common";
import { useRewardsColor } from "./util";

export function RewardsStrategyApy({ strategyExpanded }: { strategyExpanded: StrategyExpanded }) {
    const { getStrategyRewards } = usePortfolioRewardsUtils();
    const { rewardPointsPerSecond } = useRewardsPointsPerSecond();

    const { data: collateralYields } = useLatestCollateralYieldHistoryByMint(
        strategyExpanded.principalMetadata.mint ? [strategyExpanded.principalMetadata.mint] : undefined
    );

    const { loanMultipliers } = useLoanMultipliersUtil();
    const { prime } = useAppPalette();
    const { utilization, apyWeighted: strategyApyWeighted } = calculateStrategyUtilization(strategyExpanded);

    const strategyRewards = getStrategyRewards(strategyExpanded.strategy.address);

    const strategyLoopscaleActions = getStrategyLoopscaleActions(strategyRewards);

    const color = useRewardsColor(
        loanMultipliers,
        strategyExpanded.principalMetadata ? [strategyExpanded.principalMetadata.mint] : undefined,
        strategyLoopscaleActions
    );

    const isIdleYield = isStrategyMarginFi(strategyExpanded);

    const apyFixed = strategyExpanded.strategy.fixedApy;
    const apyVariable = strategyExpanded?.externalYieldInfo?.apy;
    const collateralYield = collateralYields?.[strategyExpanded.principalMetadata.mint];
    const netApy = strategyApyWeighted + (collateralYield ?? 0);

    // Calculate total lender reward multiplier from active rewards
    const lendMultiplier = getStrategyLendMultiplier(strategyRewards);
    const idleCapMultiplier = getStrategyIdleCapMultiplier(strategyRewards);

    // Get reference points per second for idle capital
    const idlePointsPerSecond = rewardPointsPerSecond[LoopscalePointsAction.IdleCap]?.pointsPerSecond;

    // Calculate relative multipliers compared to live idle capital rate
    const lendMultiplierRelativeToLiveIdleCap = idlePointsPerSecond
        ? roundAutoToDecimals(lendMultiplier / idlePointsPerSecond)
        : 0;
    const idleCapMultiplierRelativeToLiveIdleCap = idlePointsPerSecond
        ? roundAutoToDecimals(idleCapMultiplier / idlePointsPerSecond)
        : 0;

    const isBoosted = color === prime;

    const customDailyPoints = calculateActiveStrategyDailyPoints(strategyRewards);

    return (
        <Tooltip
            noPadding
            reverseColors
            title={
                <StrategyRateAndRewardTooltip
                    apyFixed={apyFixed}
                    utilization={utilization}
                    lendMultiplier={lendMultiplierRelativeToLiveIdleCap}
                    allocation={1 - utilization}
                    apyVariable={apyVariable}
                    idleMultiplier={idleCapMultiplierRelativeToLiveIdleCap}
                    totalDailyRewards={customDailyPoints}
                    netApy={netApy}
                    collateralYield={collateralYield}
                    isBoosted={isBoosted}
                    isIdleCapEligible={isIdleYield}
                    principalMetadata={strategyExpanded.principalMetadata}
                />
            }
        >
            <Row spacing={1}>
                <ExternalRewardsDisplay
                    metadata={strategyExpanded.principalMetadata}
                    loopscalePointsAction={LoopscalePointsAction.Lend}
                />
                <Text underline>{formatPercent(netApy)} APY</Text>
            </Row>
        </Tooltip>
    );
}

export function isStrategyMarginFi(strategyExpanded: StrategyExpanded | undefined) {
    if (!strategyExpanded) return false;
    return (
        strategyExpanded.externalYieldInfo?.externalYieldSource === ExternalYieldSource.MarginFi &&
        strategyExpanded.externalYieldInfo.balance > 0
    );
}

type StrategyRateAndRewardTooltipProps = {
    isBoosted: boolean;
    isIdleCapEligible: boolean;
    principalMetadata: TokenListMetadata;
} & TooltipLoansProps &
    TooltipIdleCapitalProps &
    TooltipSummaryProps;

function StrategyRateAndRewardTooltip(props: StrategyRateAndRewardTooltipProps) {
    const {
        apyFixed,
        utilization,
        lendMultiplier,
        allocation,
        apyVariable,
        idleMultiplier,
        totalDailyRewards,
        netApy,
        isBoosted,
        isIdleCapEligible,
        collateralYield,
        principalMetadata
    } = props;

    return (
        <TooltipContent px={0} py={1.5} spacing={1}>
            <TooltipLoans apyFixed={apyFixed} utilization={utilization} lendMultiplier={lendMultiplier} />
            <Divider />
            {isIdleCapEligible && (
                <>
                    <TooltipIdleCapital
                        allocation={allocation}
                        apyVariable={apyVariable}
                        idleMultiplier={idleMultiplier}
                    />
                    <Divider />
                </>
            )}

            <TooltipSummary
                totalDailyRewards={totalDailyRewards}
                netApy={netApy}
                isBoosted={isBoosted}
                collateralYield={collateralYield}
                principalMetadata={principalMetadata}
            />
        </TooltipContent>
    );
}
interface TooltipLoansProps {
    apyFixed: number | undefined;
    utilization: number | undefined;
    lendMultiplier: number | undefined;
}

function TooltipLoans({ apyFixed, utilization, lendMultiplier }: TooltipLoansProps) {
    const stats: Hidable<StatProps>[] = [
        {
            value: formatPercent(utilization),
            caption: "Utilization"
        },
        {
            value: formatMultiplier(lendMultiplier),
            caption: "Lend boost",
            hide: !lendMultiplier
        },
        {
            value: formatPercent(apyFixed),
            caption: "APY (fixed rate)"
        }
    ].filter((s) => !s.hide);
    return (
        <Column spacing={1} px={1.5}>
            <Text variant="body2" fontWeight="bold">
                Deployed
            </Text>
            <StatColumn stats={stats} variant="body2" />
        </Column>
    );
}

interface TooltipIdleCapitalProps {
    allocation: number | undefined;
    apyVariable: number | undefined;
    idleMultiplier: number | undefined;
}

function TooltipIdleCapital({ allocation, idleMultiplier, apyVariable }: TooltipIdleCapitalProps) {
    const stats: Hidable<StatProps>[] = [
        {
            value: formatPercent(allocation),
            caption: "Allocation"
        },
        {
            value: formatMultiplier(idleMultiplier),
            caption: "Idle capital boost",
            hide: !idleMultiplier
        },
        {
            value: formatPercent(apyVariable),
            caption: "APY (variable)"
        }
    ].filter((s) => !s.hide);
    return (
        <Column spacing={1} px={1.5}>
            <Text variant="body2" fontWeight="bold">
                Idle
            </Text>
            <StatColumn stats={stats} variant="body2" />
        </Column>
    );
}

interface TooltipSummaryProps {
    totalDailyRewards: number | undefined;
    collateralYield?: number | undefined;
    netApy: number | undefined;
}
function TooltipSummary({
    totalDailyRewards,
    netApy,
    isBoosted,
    collateralYield,
    principalMetadata
}: TooltipSummaryProps & { isBoosted: boolean; principalMetadata: TokenListMetadata }) {
    const stats: Hidable<StatProps>[] = [
        {
            value: (
                <StatWithIcon
                    value={formatNum(totalDailyRewards)}
                    icon={<RewardIcon hasMultiplier={isBoosted} />}
                    captionColor="body"
                    variant="body2"
                />
            ),
            caption: COPY.DAILY_REWARDS_TERM,
            hide: false
        },
        {
            value: formatPercent(collateralYield),
            caption: `${BsMetaUtil.getSymbol(principalMetadata)} APY`,
            hide: !collateralYield
        },
        {
            value: formatPercent(netApy),
            caption: "Net APY",
            textColor: getNetApyColor(netApy),
            hide: false
        }
    ].filter((s) => !s.hide);

    return (
        <Column px={1.5}>
            <StatColumn stats={stats} variant="body2" />
        </Column>
    );
}

function getNetApyColor(netApy: number | undefined): TextColor {
    if (netApy === undefined) return "disabled";
    if (netApy > 0) return "success";
    if (netApy < 0) return "error";
    return "disabled";
}
